import { t } from "i18next";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { setLoading } from "store/appSlice";
import { formatDateToZ } from "util/datelib";
import GenericCard from "widgets/cards/generic";
import "@react-pdf-viewer/core/lib/styles/index.css";

import CircleButton from "widgets/controls/buttons/circle-button";
import { MdAdd, MdCheckCircleOutline, MdFileDownload } from "react-icons/md";
import { IoMdCloseCircleOutline } from "react-icons/io";

import { ENDPOINTS } from "api/endpoints";
import { ReportConfiguration } from "models/reports/configuration";
import { AdhocReqBody } from "api/types/report";
import { reportAdhoc } from "api/report";
import { resolveHourlyReportCase4 } from "util/hourlyAggregate";

interface MyDocumentProps {
  data: any;
  onSuccess: (data: any) => void;
  step: number;
  setStep: (step: number) => void;
}

const ViewerPdf: React.FC<MyDocumentProps> = ({
  data,
  onSuccess,
  step,
  setStep,
}) => {
  const [files, setFiles] = useState<any>();
  const [dateRange] = useState<any>([new Date(), new Date()]);
  const dispatch = useDispatch();

  // Generate Adhoc report
  const generateAdhocReport = useCallback(
    async (
      endpoint: string,
      type?: AdhocReqBody["type"],
      bucket?: "hour",
      sensors?: string[],
      classes?: string[]
    ) => {
      dispatch(setLoading(true));
      try {
        const body: AdhocReqBody = {
          siteId: data?.site || undefined,
          snr: data?.device || undefined,
          from: formatDateToZ(dateRange[0], false, "day", data.timezone),
          to: formatDateToZ(dateRange[1], true, "day", data.timezone),
          timezone: data?.timezone || undefined,
          email: data?.email || undefined,
          type: type || undefined,
          bucket: bucket || undefined,
          sensors: sensors || undefined,
          classes: classes || undefined,
          snrs:
            data?.devices && data.devices.length > 0 ? data.devices : undefined,
          models:
            data?.models && data.models.length > 0 ? data.models : undefined,
          deviceModel: data?.deviceModel || undefined,
          isTest: true,
        };
        const cleanedBody = Object.fromEntries(
          Object.entries(body).filter(([_, v]) => v !== undefined)
        ) as AdhocReqBody;

        if (data.devices?.length > 10) {
          cleanedBody.snrs = data.devices.slice(0, 10);
        }

        if (data?.all) {
          body.snrs = [];
        }

        const { data: res, status } = await reportAdhoc(cleanedBody, endpoint);
        if (status === 200) {
          setFiles(res.url);
          handleSuccess(body, endpoint);
        }
      } finally {
        dispatch(setLoading(false));
      }
    },
    [data, dispatch, dateRange]
  );

  useEffect(() => {
    if (!data) return;
    setFiles(null);

    switch (data.reportType) {
      case 1:
        generateAdhocReport(ENDPOINTS.REPORT.RAW);
        break;
      case 2:
        generateAdhocReport(ENDPOINTS.REPORT.DATA.USAGE.BASIC, "WATER");
        break;
      case 3:
        generateAdhocReport(ENDPOINTS.REPORT.DATA.USAGE.BASIC, "ELECTRICITY");
        break;
      case 4: {
        const spec = resolveHourlyReportCase4(data?.deviceModel);
        if (spec.mode === "average_timeseries") {
          generateAdhocReport(
            ENDPOINTS.REPORT.DATA.AVERAGE.TIME_SERIES,
            spec.metric,
            "hour"
          );
        } else {
          generateAdhocReport(
            ENDPOINTS.REPORT.DATA.USAGE.AGGREGATE,
            undefined,
            "hour",
            [...spec.sensors]
          );
        }
        break;
      }
      case 5:
        generateAdhocReport(ENDPOINTS.REPORT.DATA.UPTIME);
        break;
      case 6:
        generateAdhocReport(ENDPOINTS.REPORT.DATA.USAGE.BASIC, "GAS");
        break;
      case 7:
        generateAdhocReport(ENDPOINTS.REPORT.INCIDENT);
        break;
      case 8:
        generateAdhocReport(
          ENDPOINTS.REPORT.ALERTS,
          undefined,
          undefined,
          undefined,
          [data.site]
        );
        break;
      case 9:
        generateAdhocReport(ENDPOINTS.REPORT.DATA.UPTIME_AUDIT);
        break;
      case 10:
        generateAdhocReport(ENDPOINTS.REPORT.DATA.USAGE.BASIC, "ELECTRICITY");
        break;
      case 11:
        generateAdhocReport(ENDPOINTS.REPORT.DATA.USAGE.RETICULATION, "WATER");
        break;
      case 12:
        generateAdhocReport(
          ENDPOINTS.REPORT.DATA.USAGE.RETICULATION,
          "ELECTRICITY"
        );
        break;
      default:
        //No report type
        break;
    }
  }, [data, dispatch, generateAdhocReport]);

  const handleSuccess = (body: any, endpoint: string) => {
    const cleanedBody = Object.fromEntries(
      Object.entries(body).filter(([_, v]) => v !== undefined)
    ) as AdhocReqBody;

    const configuration = {
      type: ReportConfiguration.find((r) => r.id === data.reportType)?.name,
      endpoint: endpoint,
      body: cleanedBody,
    };

    setStep(1);

    onSuccess(configuration);
  };

  // Download PDF file
  const downloadPdf = () => {
    const link = document.createElement("a");
    link.href = files.pdfUrl;
    link.target = "_blank";
    link.download = "report.pdf";
    link.click();
  };

  // Download Excel file
  const downloadExcel = () => {
    const link = document.createElement("a");
    link.href = files.excelUrl;
    link.target = "_blank";
    link.download = "report.xlsx";
    link.click();
  };

  return (
    <>
      <GenericCard
        title={t("reports.schedule.title")}
        description={t("reports.schedule.description")}
        // extra="h-full"
        topRight={
          step === 3 && (
            <div className="flex flex-col gap-2 md:flex-row">
              <CircleButton
                size="sm"
                variant="outline"
                icon={MdAdd}
                label={t("button.schedule.new")}
                onClick={() => {
                  setStep(0);
                  onSuccess(null);
                }}
                iconPosition="right"
              />
              {files?.pdfUrl && (
                <CircleButton
                  size="sm"
                  variant="outline"
                  icon={MdFileDownload}
                  label={t("button.download.pdf")}
                  onClick={downloadPdf}
                  iconPosition="right"
                />
              )}
              {files?.excelUrl && (
                <CircleButton
                  size="sm"
                  variant="outline"
                  icon={MdFileDownload}
                  label={t("button.download.excel")}
                  onClick={downloadExcel}
                  iconPosition="right"
                />
              )}
            </div>
          )
        }
      >
        <div className="grid-col-1 grid h-fit px-4 pt-4 md:grid-cols-5">
          <div className="text-md flex flex-col items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white">
              1
            </div>
            <span className="text-center text-secondary">
              {t("reports.step.build.title")}
            </span>
          </div>
          <div className="flex items-center justify-center gap-2">
            <div className="mx-4 h-1 w-full rounded-full bg-secondary" />
            {step === 1 || step > 2 ? (
              <MdCheckCircleOutline className="h-32 w-32 text-green-500" />
            ) : (
              <IoMdCloseCircleOutline
                className="h-32 w-32 text-red-500"
                color="secondary"
              />
            )}
            <div className="mx-4 h-1 w-full rounded-full bg-secondary" />
          </div>
          <div className="text-md flex flex-col items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white">
              2
            </div>
            <span className="text-center text-secondary">
              {t("reports.step.schedule.title")}
            </span>
          </div>
          <div className="flex items-center justify-center gap-2">
            <div className="mx-4 h-1 w-full rounded-full bg-secondary" />
            {step === 2 || step > 2 ? (
              <MdCheckCircleOutline className="h-32 w-32 text-green-500" />
            ) : (
              <IoMdCloseCircleOutline
                className="h-32 w-32 text-red-500"
                color="secondary"
              />
            )}
            <div className="mx-4 h-1 w-full rounded-full bg-secondary" />
          </div>
          <div className="text-md flex flex-col items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white">
              3
            </div>
            <span className="text-center text-secondary">
              {t("reports.step.preview.title")}
            </span>
          </div>
        </div>
      </GenericCard>
    </>
  );
};

export default ViewerPdf;
